import { PieChartOptions, ResponsiveOptions } from 'chartist'
import classNames from 'classnames'
import React, { FC } from 'react'
import { Breakpoints, resolveBreakpointName } from '../../utilities/breakpoints'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { ChartistDonutGraph } from './Chartist/ChartistDonutGraph/ChartistDonutGraph'
import { LegendCircle } from './Chartist/ChartistDonutGraph/LegendCircle'

export interface GraphPoint {
  label: string
  footnotes?: number[]
  value: number
}

export interface InfoItem {
  label: string
  value: string
}

export interface DonutGraphCardProps {
  footNote?: string
  additionalInfo?: InfoItem[]
  data: GraphPoint[]
}

export const options: PieChartOptions = {
  donut: true,
  donutWidth: 22,
  // Note: chartPadding is necessary, otherwise labels will be clipped
  chartPadding: 75,
  width: '320px',
  height: '320px',
  labelOffset: 25,
  labelDirection: 'explode',
}

export const responsiveOptions: ResponsiveOptions<PieChartOptions> = [
  [
    `screen and (min-width: ${resolveBreakpointName(Breakpoints.tb)}px)`,
    {
      chartPadding: 120,
      donutWidth: 30,
      width: '495px',
      height: '495px',
    },
  ],
  [
    `screen and (min-width: ${resolveBreakpointName(Breakpoints.xl)}px)`,
    {
      chartPadding: 160,
      labelOffset: 35,
      width: '590px',
      height: '590px',
    },
  ],
]

export const DonutGraphCard: FC<DonutGraphCardProps> = ({
  footNote,
  additionalInfo,
  data,
}) => (
  <div className="flex flex-col border border-primary rounded-15">
    {!!footNote?.length && (
      <div className="self-left flex flex-col pt-15 pl-15">
        <div className="font-t text-t-200 text-secondary-darker">{footNote}</div>
      </div>
    )}

    <div
      className={classNames(
        // Label styling in mobile
        '[&_.ct-label]:font-t [&_.ct-label]:text-t-400 [&_.ct-label]:fill-secondary'
      )}
    >
      <div
        className={classNames(
          // Counteract chartPadding with negative margins
          'self-center xl:-mt-60 xl:-mb-120'
        )}
      >
        <ChartistDonutGraph
          data={{
            labels: data.map(d => {
              let value = `~${d.value}%`

              if (d.footnotes?.length) {
                value = `${value}[${d.footnotes.join(',')}]`
              }

              return value
            }),
            series: data.map(d => d.value),
          }}
          labels={data.map(d => d.label)}
          options={options}
          responsiveOptions={responsiveOptions}
        />
      </div>

      <div className="flex flex-col gap-45 px-45 pb-45 tb:px-60 tb:pb-60">
        <div className="flex flex-col self-center gap-15 tb:hidden">
          {data.map((d, index) => (
            <div className="flex gap-10 items-center" key={index}>
              <LegendCircle index={index} />{' '}
              <span className="font-t text-t-300 text-secondary-darker">{d.label}</span>
            </div>
          ))}
        </div>

        {!!additionalInfo?.length && (
          <div className="flex flex-col gap-45">
            <hr className="bg-neutral-lighter" />

            <div className="flex flex-col gap-20 tb:flex-row tb:flex-wrap tb:justify-center">
              {additionalInfo.map((metric, index) => (
                <div
                  className="flex flex-col items-center gap-5 px-20 tb:flex-grow-0 tb:flex-[calc(50%-10px)]"
                  key={index}
                >
                  {!isNullOrWhiteSpace(metric.value) && (
                    <div className="font-t text-t-900 text-secondary md:text-t-700 xl:text-t-900">
                      {metric.value}
                    </div>
                  )}

                  {!isNullOrWhiteSpace(metric.label) && (
                    <div className="font-t text-t-500 text-center text-secondary-darker md:text-t-400 xl:text-t-500">
                      {metric.label}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
)
